/**
 * Universal-Welten-Deduplizierung (Wizard-Schritt 4, 22.09.2026).
 *
 * Betreiberauftrag: KI-Themen (Schritt 2), die sich thematisch mit einer der
 * verfügbaren Universal-Welten überschneiden, werden VOLLSTÄNDIG aus der Auswahl
 * entfernt (keine Zeile, keine Checkbox, kein Wiederhinzufügen). Die Universal-Welt
 * bleibt die kanonische Quelle (kostenlos, sofort verfügbar). Rein clientseitig —
 * setup/complete, suggest-worlds, Adopt-Endpoint und KI-Logik bleiben unberührt.
 *
 * Abgleich = signifikanter Schlüsselwort-Vergleich (NICHT bloße String-Normalisierung):
 * Titel werden normalisiert (Kleinschreibung, Interpunktion → Leerraum) und um
 * generische Füllwörter bereinigt; ein Treffer liegt vor bei
 *   - Gleichheit der signifikanten Wortmengen, ODER
 *   - Teilmengenbeziehung, sofern die kleinere Menge >= 2 signifikante Wörter hat.
 * Die >=2-Bedingung verhindert False-Positives bei generischen Einzelwörtern
 * (z. B. "Pflege" NICHT ⊆ "Verbandskasten – Bereitstellung und Pflege").
 */

const STOPWORDS = new Set([
  // Artikel / Präpositionen / Konjunktionen
  "der", "die", "das", "den", "dem", "des", "ein", "eine", "einer", "eines",
  "einem", "einen", "und", "oder", "im", "in", "am", "an", "auf", "bei", "mit",
  "von", "zu", "zur", "zum", "für", "aus", "über", "unter", "nach", "vor",
  "durch", "um", "sowie", "bzw", "je", "als",
  // generische Arbeitsschutz-Füllwörter (exakte Tokens, KEINE Komposita)
  "arbeit", "arbeiten", "arbeitsplatz", "arbeitsplätze",
  "betrieb", "betriebe", "betrieben", "betrieblich", "betriebliche",
  "betrieblichen",
]);

export function significantTokens(title) {
  return (title || "")
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter((t) => t && !STOPWORDS.has(t));
}

function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

function isSubset(small, large) {
  for (const x of small) if (!large.has(x)) return false;
  return true;
}

/**
 * true, wenn sich zwei Titel thematisch überschneiden (siehe Modul-Doku).
 */
export function titlesOverlap(a, b) {
  const A = new Set(significantTokens(a));
  const B = new Set(significantTokens(b));
  if (A.size === 0 || B.size === 0) return false; // Leere-Menge-Falle vermeiden
  if (setsEqual(A, B)) return true;
  const [small, large] = A.size <= B.size ? [A, B] : [B, A];
  if (small.size >= 2 && isSubset(small, large)) return true;
  return false;
}

/**
 * Liefert die Menge der KI-Vorschlagstitel, die sich mit MINDESTENS EINER der
 * verfügbaren Universal-Welten überschneiden (Abgleich gegen ALLE, unabhängig vom
 * Auswahl-Status). Diese Titel werden aus der Wizard-Auswahl entfernt.
 */
export function findDuplicateTopicTitles(suggestionTitles, universalTitles) {
  const dupes = new Set();
  const unis = universalTitles || [];
  for (const s of suggestionTitles || []) {
    for (const u of unis) {
      if (titlesOverlap(s, u)) {
        dupes.add(s);
        break;
      }
    }
  }
  return dupes;
}
